import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, CircleCheck as CheckCircle2, Circle as XCircle, Package, MapPin, ScanLine, TriangleAlert as AlertTriangle, ArrowRight, Loader as Loader2, Volume2, VolumeX, Camera, Zap, Navigation, ImageOff, Printer, Keyboard } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getValidToken } from '../../lib/auth';
import type { ShopifyOrder, ShopifyOrderLineItem, FinishOrderResponse } from '../../types/shopify';
import {
  loadWorkerProductLocations,
  resolveWorkerProduct,
} from '../../lib/services/binInventoryService';
import { classifyOrderIssue } from '../../utils/classifyOrderIssue';
import { checkShopifyOrderLiveStatus } from '../../utils/shopifyOrderLiveStatus';
import { getPickableLineItems } from '../../utils/pickableLineItems';
import { getAdminId } from '../../utils/getAdminId';
import { classifyScanValue } from '../../utils/scanClassify';
import { resolveWedgeScan } from '../../utils/resolveWedgeScan';

type ImageState = 'loading' | 'loaded' | 'error';

interface PickItem extends ShopifyOrderLineItem {
  status: 'pending' | 'picked' | 'skipped';
  binLocation: string | null;
  productId: string | null;
  barcode: string | null;
  imageUrl: string | null;
  colorName: string | null;
  sizeName: string | null;
}

interface PickModalProps {
  order: ShopifyOrder;
  onClose: () => void;
  onComplete: () => void;
}

export const PickModal: React.FC<PickModalProps> = ({ order, onClose, onComplete }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<PickItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [finishResult, setFinishResult] = useState<FinishOrderResponse | null>(null);
  const [scanInput, setScanInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanWarning, setScanWarning] = useState<string | null>(null);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [scannerActive, setScannerActive] = useState(true);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [imageState, setImageState] = useState<ImageState>('loading');
  const inputRef = useRef<HTMLInputElement>(null);
  const successAudioRef = useRef<HTMLAudioElement | null>(null);
  const errorAudioRef = useRef<HTMLAudioElement | null>(null);
  const scanBufferRef = useRef<string>('');
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScanTimeRef = useRef<number>(0);
  const lastScanValueRef = useRef<string>('');
  const handleScanFromBufferRef = useRef<(scannedValue: string) => void>();

  const adminId = getAdminId(user);

  useEffect(() => {
    successAudioRef.current = new Audio('data:audio/wav;base64,UklGRl9vT19teleR0');
    errorAudioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZF');

    successAudioRef.current.volume = 0.5;
    errorAudioRef.current.volume = 0.5;
  }, []);

  const playSound = useCallback((type: 'success' | 'error') => {
    if (!soundEnabled) return;
    try {
      if (type === 'success' && successAudioRef.current) {
        successAudioRef.current.currentTime = 0;
        successAudioRef.current.play().catch(() => {});
      } else if (type === 'error' && errorAudioRef.current) {
        errorAudioRef.current.currentTime = 0;
        errorAudioRef.current.play().catch(() => {});
      }
    } catch {}
  }, [soundEnabled]);

  const vibrate = useCallback((pattern: number | number[]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  useEffect(() => {
    const loadItemsWithLocations = async () => {
      if (!adminId || !order.line_items) {
        setLoading(false);
        return;
      }

      try {
        const pickableItems = getPickableLineItems(order.line_items);

        const skus = pickableItems
          .map((item) => item.sku)
          .filter((sku): sku is string => sku !== null && sku !== '');

        const locationResult = await loadWorkerProductLocations(adminId, skus);
        setLookupFailed(!locationResult.ok);

        const pickItems: PickItem[] = pickableItems.map((item) => {
          const productInfo = resolveWorkerProduct(locationResult, item.sku);
          const productId = productInfo?.id || null;
          const binLocation = productId
            ? locationResult.locationMap.get(productId) || null
            : null;

          return {
            ...item,
            status: 'pending',
            binLocation,
            productId,
            barcode: productInfo?.barcode || null,
            imageUrl: productInfo?.image_url || null,
            colorName: productInfo?.color_name || null,
            sizeName: productInfo?.size_name || null,
          };
        });

        pickItems.sort((a, b) => {
          if (a.binLocation && !b.binLocation) return -1;
          if (!a.binLocation && b.binLocation) return 1;
          if (a.binLocation && b.binLocation) {
            return a.binLocation.localeCompare(b.binLocation);
          }
          return 0;
        });

        setItems(pickItems);
      } catch (err) {
        console.error('Failed to load items:', err);
        setLookupFailed(true);
        const pickItems: PickItem[] = getPickableLineItems(order.line_items).map((item) => ({
          ...item,
          status: 'pending',
          binLocation: null,
          productId: null,
          barcode: null,
          imageUrl: null,
          colorName: null,
          sizeName: null,
        }));
        setItems(pickItems);
      } finally {
        setLoading(false);
      }
    };

    loadItemsWithLocations();
  }, [adminId, order.line_items]);

  // Only take focus when the operator asked to type. Focusing a text field on a
  // tablet opens the on-screen keyboard, which covers the pick information;
  // hardware scans are captured by the window listener below instead.
  useEffect(() => {
    if (!loading && isManualEntry) inputRef.current?.focus();
  }, [loading, currentIndex, isManualEntry]);

  useEffect(() => {
    if (!loading && scannerActive) {
      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
          return;
        }

        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }

        const now = Date.now();

        if (e.key === 'Enter') {
          e.preventDefault();
          const currentBuffer = scanBufferRef.current;

          if (currentBuffer.length > 0) {
            const kind = classifyScanValue(currentBuffer);
            const dedupeMs = kind === 'epc' ? 1200 : 150;
            const timeSinceLastScan = now - lastScanTimeRef.current;
            const isDuplicate =
              timeSinceLastScan < dedupeMs && currentBuffer === lastScanValueRef.current;

            if (!isDuplicate) {
              lastScanTimeRef.current = now;
              lastScanValueRef.current = currentBuffer;
              setScanInput(currentBuffer);
              setTimeout(() => handleScanFromBufferRef.current?.(currentBuffer), 50);
            }
            scanBufferRef.current = '';
          }
        } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
          scanBufferRef.current += e.key;

          scanTimeoutRef.current = setTimeout(() => {
            scanBufferRef.current = '';
          }, 300);
        }
      };

      window.addEventListener('keypress', handleKeyPress);
      return () => {
        window.removeEventListener('keypress', handleKeyPress);
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
      };
    }
  }, [loading, scannerActive]);

  const currentItem = items[currentIndex];
  const pickedCount = items.filter((i) => i.status === 'picked').length;
  const progress = items.length > 0 ? (pickedCount / items.length) * 100 : 0;

  // Image visibility must follow the URL, not the pick transition. Forcing
  // imageLoading=true on advance left opacity-0 forever when the next item
  // shared the same src (no load event) or when the last item could not advance.
  useEffect(() => {
    if (!currentItem?.imageUrl) {
      setImageState('error');
      return;
    }
    setImageState('loading');
  }, [currentItem?.imageUrl]);

  const handleImageRef = useCallback((node: HTMLImageElement | null) => {
    if (!node) return;
    if (node.complete && node.naturalWidth > 0) setImageState('loaded');
  }, []);

  const validateScan = useCallback(
    (scannedValue: string) => {
      if (!currentItem) return false;

      const normalizedScan = scannedValue.trim().toLowerCase();
      const skuMatch = currentItem.sku?.toLowerCase() === normalizedScan;
      const barcodeMatch = currentItem.barcode?.toLowerCase() === normalizedScan;

      return skuMatch || barcodeMatch;
    },
    [currentItem]
  );

  const matchesCurrentItem = useCallback(
    (params: { value?: string; productId?: string | null; sku?: string | null; barcode?: string | null }) => {
      if (!currentItem) return false;
      if (params.productId && currentItem.productId && params.productId === currentItem.productId)
        return true;
      const candidates = [params.value, params.sku, params.barcode]
        .filter(Boolean)
        .map((v) => String(v).trim().toLowerCase());
      return candidates.some(
        (v) =>
          currentItem.sku?.toLowerCase() === v ||
          currentItem.barcode?.toLowerCase() === v
      );
    },
    [currentItem]
  );

  const markCurrentPicked = useCallback(() => {
    playSound('success');
    vibrate(100);
    setScanSuccess(true);
    setScanError(null);

    setItems((prev) =>
      prev.map((item, idx) =>
        idx === currentIndex ? { ...item, status: 'picked' } : item
      )
    );

    setTimeout(() => {
      setScanInput('');
      setScanSuccess(false);
      setScanWarning(null);
      if (currentIndex < items.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      }
    }, 600);
  }, [currentIndex, items.length, playSound, vibrate]);

  const handleScanFromBuffer = useCallback(async (scannedValue: string) => {
    if (!scannedValue.trim() || !currentItem) return;

    setScanError(null);
    setScanWarning(null);
    setScanSuccess(false);

    const result = await resolveWedgeScan(adminId, scannedValue);

    if (result.kind === 'barcode') {
      if (validateScan(result.value)) {
        markCurrentPicked();
      } else {
        playSound('error');
        vibrate([100, 50, 100]);
        setScanError(
          `Wrong item! Expected: ${currentItem.sku || currentItem.title}`
        );
        setScanInput('');
      }
      return;
    }

    if (result.kind === 'unknown' || result.kind === 'epc_error') {
      playSound('error');
      vibrate([100, 50, 100]);
      setScanError(result.message);
      setScanInput('');
      return;
    }

    // epc
    if (result.integrityAlarm) {
      playSound('error');
      vibrate([100, 50, 100]);
      setScanError(result.message || 'DATA INTEGRITY ALARM — unit record and EPC disagree');
      setScanInput('');
      return;
    }

    const isMatch = matchesCurrentItem({
      value: result.value,
      productId: result.item.id,
      sku: result.item.sku,
      barcode: result.item.barcode,
    });

    if (!isMatch) {
      playSound('error');
      vibrate([100, 50, 100]);
      setScanError(
        `Wrong item! Expected: ${currentItem.sku || currentItem.title}`
      );
      setScanInput('');
      return;
    }

    if (!result.unitFound) {
      setScanWarning(
        result.message ||
          'No unit record for this EPC. Variant matched from EPC base.'
      );
    }

    markCurrentPicked();
  }, [
    adminId,
    currentItem,
    markCurrentPicked,
    matchesCurrentItem,
    playSound,
    validateScan,
    vibrate,
  ]);

  handleScanFromBufferRef.current = handleScanFromBuffer;

  const handleScan = useCallback(() => {
    void handleScanFromBuffer(scanInput);
  }, [scanInput, handleScanFromBuffer]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const handleManualConfirm = () => {
    if (!currentItem) return;

    playSound('success');
    vibrate(100);
    setScanSuccess(true);

    setItems((prev) =>
      prev.map((item, idx) =>
        idx === currentIndex ? { ...item, status: 'picked' } : item
      )
    );

    setTimeout(() => {
      setScanInput('');
      setScanSuccess(false);
      if (currentIndex < items.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      }
    }, 400);
  };

  const handleSkipItem = () => {
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === currentIndex ? { ...item, status: 'skipped' } : item
      )
    );
    setScanInput('');
    setScanError(null);
    if (currentIndex < items.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleComplete = async () => {
    if (!user || !adminId) return;

    setCompleting(true);
    setFinishError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('update_shopify_order_ready_status', {
        p_order_id: order.id,
        p_admin_id: adminId,
        p_is_ready: true,
        p_user_id: user.id,
      });

      if (rpcError) throw rpcError;

      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to mark order as ready');
      }

      await supabase.from('pick_sessions').insert({
        admin_id: adminId,
        order_id: order.id,
        picker_id: user.id,
        status: 'completed',
        completed_at: new Date().toISOString(),
        total_items: items.length,
        picked_items: items.filter((i) => i.status === 'picked').length,
      });

      onComplete();
    } catch (err) {
      console.error('Failed to complete order:', err);
      setFinishError(err instanceof Error ? err.message : 'Failed to mark order as ready');
    } finally {
      setCompleting(false);
    }
  };

  const handleFinishOrder = async () => {
    if (!user || !adminId) return;

    setFinishing(true);
    setFinishError(null);
    setFinishResult(null);

    try {
      const liveStatus = await checkShopifyOrderLiveStatus(order.shopify_order_id);
      if (liveStatus.error) {
        throw new Error(liveStatus.error);
      }
      if (liveStatus.cancelled) {
        setFinishError(`Order ${order.order_number} was cancelled in Shopify and removed from the queue.`);
        onComplete();
        return;
      }

      const token = await getValidToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Mark order as ready first (required by shopify-finish-order API)
      const { data: readyData, error: readyError } = await supabase.rpc('update_shopify_order_ready_status', {
        p_order_id: order.id,
        p_admin_id: adminId,
        p_is_ready: true,
        p_user_id: user.id,
      });

      if (readyError) throw readyError;

      const readyResult = readyData as { success: boolean; error?: string };
      if (!readyResult.success) {
        throw new Error(readyResult.error || 'Failed to mark order as ready');
      }

      // Only the lines the picker confirmed get fulfilled on Shopify; skipped lines stay
      // unfulfilled so the order becomes PARTIALLY_FULFILLED (FULFILLED when all are picked).
      const pickedLineItems = items
        .filter((i) => i.status === 'picked')
        .map((i) => ({ id: i.id, sku: i.sku, quantity: i.quantity }));

      // Now proceed with finish order API call
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-finish-order`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            shopifyOrderId: order.shopify_order_id,
            notifyCustomer: true,
            pickedLineItems,
          }),
          signal: AbortSignal.timeout(60000),
        }
      );

      const text = await response.text();
      let data: FinishOrderResponse | null = null;
      try {
        data = text ? (JSON.parse(text) as FinishOrderResponse) : null;
      } catch {
        if (!response.ok) {
          console.error('Finish order response (non-JSON):', text?.slice(0, 200));
          const msg = response.status === 401 ? 'Authentication failed. Please log in again.' : 'Request failed (400). Please try again or contact support.';
          throw new Error(msg);
        }
      }

      if (!response.ok) {
        const defaultMessage = response.status === 401
          ? 'Authentication failed. Please log in again.'
          : 'Request failed (400). Please try again or contact support.';
        const errorMessage = (data && typeof data.error === 'string') ? data.error : defaultMessage;
        if (!data) console.error('Finish order response (non-JSON):', text?.slice(0, 200));
        throw new Error(errorMessage);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to finish order');
      }

      setFinishResult(data);

      // Print label if available
      if (data.labelPdf) {
        try {
          const blob = new Blob(
            [Uint8Array.from(atob(data.labelPdf), c => c.charCodeAt(0))],
            { type: 'application/pdf' }
          );
          const url = URL.createObjectURL(blob);
          const printWindow = window.open(url, '_blank');
          if (printWindow) {
            printWindow.onload = () => {
              printWindow.print();
              URL.revokeObjectURL(url);
            };
            // Fallback: try printing after a delay if onload doesn't fire
            setTimeout(() => {
              if (printWindow && !printWindow.closed) {
                try {
                  printWindow.print();
                } catch (e) {
                  console.error('Error printing:', e);
                }
                URL.revokeObjectURL(url);
              }
            }, 1000);
          } else {
            console.warn('Print window was blocked');
          }
        } catch (printError) {
          console.error('Error printing label:', printError);
          // Don't fail the whole operation if printing fails
        }
      }

      // Log pick session
      const { error: pickSessionError } = await supabase.from('pick_sessions').insert({
        admin_id: adminId,
        order_id: order.id,
        picker_id: user.id,
        status: 'completed',
        completed_at: new Date().toISOString(),
        total_items: items.length,
        picked_items: items.filter((i) => i.status === 'picked').length,
      });
      if (pickSessionError) {
        console.error('Failed to log pick session:', pickSessionError);
      }

      if (adminId && order.order_number) {
        (async () => { try { await supabase.rpc('resolve_order_issue_by_reference', {
          p_admin_id: adminId,
          p_order_reference: order.order_number,
        }); } catch {} })();
      }

      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (err) {
      console.error('Failed to finish order:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to finish order';
      setFinishError(errorMsg);

      if (adminId && order.order_number) {
        (async () => { try { await supabase.rpc('upsert_order_issue', {
          p_admin_id: adminId,
          p_order_reference: order.order_number,
          p_shopify_order_id: order.shopify_order_id ?? null,
          p_client_name: order.customer_name ?? null,
          p_recipient_address: order.shipping_address ?? {},
          p_issue_type: classifyOrderIssue(errorMsg),
          p_issue_description: errorMsg,
          p_error_code: null,
          p_source: 'worker_terminal',
        }); } catch {} })()
      }
    } finally {
      setFinishing(false);
    }
  };

  const allItemsPicked = items.every((i) => i.status === 'picked' || i.status === 'skipped');
  const hasSkippedItems = items.some((i) => i.status === 'skipped');

  const itemJumpChips = (
    <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center">
      {items.map((item, idx) => (
        <button
          key={idx}
          onClick={() => {
            if (item.status === 'pending') {
              setCurrentIndex(idx);
              setScanInput('');
              setScanError(null);
            }
          }}
          disabled={item.status !== 'pending'}
          className={`w-10 h-10 short:w-9 short:h-9 rounded-lg flex items-center justify-center font-bold text-sm transition-all ${
            idx === currentIndex && item.status === 'pending'
              ? 'bg-cyan-500 text-white ring-2 ring-cyan-300'
              : item.status === 'picked'
              ? 'bg-green-500/30 text-green-400 border border-green-500/50'
              : item.status === 'skipped'
              ? 'bg-gray-700 text-gray-500'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700'
          }`}
          title={`Item ${idx + 1}`}
        >
          {item.status === 'picked' ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : item.status === 'skipped' ? (
            <XCircle className="h-4 w-4" />
          ) : (
            idx + 1
          )}
        </button>
      ))}
    </div>
  );

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center">
        <div className="bg-gray-900 rounded-2xl p-8 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-cyan-500 mx-auto mb-4" />
          <p className="text-gray-300 text-lg">Loading pick list...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-hidden">
      <div className="w-full max-w-3xl short:max-w-6xl max-h-full flex flex-col">
        <div className="bg-gray-900 rounded-2xl shadow-2xl w-full border border-gray-800 flex flex-col min-h-0 max-h-full">
          <div className="p-4 sm:p-6 short:py-3 border-b border-gray-800 shrink-0">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl short:text-lg font-bold text-white truncate">
                  Picking {order.order_number}
                </h2>
                <p className="text-sm sm:text-base text-gray-400 mt-1 short:mt-0 truncate">
                  Item {currentIndex + 1} of {items.length}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <button
                  onClick={() => setScannerActive(!scannerActive)}
                  className={`p-2 sm:p-3 rounded-xl transition-colors ${
                    scannerActive
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-800 text-gray-500'
                  }`}
                  title={scannerActive ? 'Scanner Active' : 'Scanner Inactive'}
                >
                  <Zap className={`h-5 w-5 sm:h-6 sm:w-6 ${scannerActive ? 'animate-pulse' : ''}`} />
                </button>
                <button
                  onClick={() => {
                    const next = !isManualEntry;
                    setIsManualEntry(next);
                    if (next) inputRef.current?.focus();
                    else inputRef.current?.blur();
                  }}
                  className={`p-2 sm:p-3 rounded-xl transition-colors ${
                    isManualEntry
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'bg-gray-800 text-gray-500'
                  }`}
                  title={isManualEntry ? 'Manual entry on - tap to return to scanner only' : 'Type manually'}
                >
                  <Keyboard className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="p-2 sm:p-3 rounded-xl bg-gray-800 text-gray-400 hover:text-white transition-colors"
                >
                  {soundEnabled ? <Volume2 className="h-5 w-5 sm:h-6 sm:w-6" /> : <VolumeX className="h-5 w-5 sm:h-6 sm:w-6" />}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 sm:p-3 rounded-xl bg-gray-800 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>

            <div className="mt-4 short:mt-2">
              <div className="h-3 short:h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 short:mt-1 text-sm short:text-xs text-gray-500">
                <span className="text-green-400 font-medium">{pickedCount} picked</span>
                <span>{items.length - pickedCount} remaining</span>
              </div>
            </div>
          </div>

          {currentItem && !allItemsPicked ? (
            <div className="flex-1 flex flex-col short:flex-row min-h-0">
              <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 p-4 sm:p-6 short:p-4">
              {lookupFailed && (
                <div className="mb-3 sm:mb-4 p-3 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Product details (photo, bin, barcode) could not be loaded. You can still pick by SKU.
                  </span>
                </div>
              )}
              {currentItem.binLocation && (
                <div className={`mb-4 sm:mb-6 short:mb-3 p-4 sm:p-6 short:p-3 rounded-2xl transition-all duration-300 ${
                  scanSuccess
                    ? 'bg-green-500/20 border-2 border-green-500'
                    : scanError
                    ? 'bg-red-500/20 border-2 border-red-500'
                    : 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-500/50'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 sm:p-3 rounded-xl ${
                      scanSuccess ? 'bg-green-500/30' : scanError ? 'bg-red-500/30' : 'bg-amber-500/30'
                    }`}>
                      {scanSuccess ? (
                        <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
                      ) : scanError ? (
                        <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-400" />
                      ) : (
                        <Navigation className="h-6 w-6 sm:h-8 sm:w-8 text-amber-400 animate-pulse" />
                      )}
                    </div>
                    <span className="text-amber-400 text-sm sm:text-base font-medium uppercase tracking-wide">
                      Go to Location
                    </span>
                  </div>
                  <div className="text-3xl sm:text-5xl md:text-6xl short:text-3xl font-black text-white tracking-wider">
                    {currentItem.binLocation}
                  </div>
                </div>
              )}

              <div
                className={`rounded-2xl p-4 sm:p-6 short:p-3 mb-4 sm:mb-6 transition-all duration-300 ${
                  scanSuccess
                    ? 'bg-green-500/10 border-2 border-green-500'
                    : scanError
                    ? 'bg-red-500/10 border-2 border-red-500'
                    : 'bg-gray-800/50 border-2 border-gray-700'
                }`}
              >
                <div className="flex flex-col sm:flex-row short:flex-row items-center gap-4 sm:gap-6 short:gap-4">
                  <div className="relative flex-shrink-0">
                    {currentItem.imageUrl && imageState !== 'error' ? (
                      <>
                        {imageState === 'loading' && (
                          <div className="absolute inset-0 w-40 h-40 sm:w-48 sm:h-48 short:w-32 short:h-32 bg-gray-700 rounded-2xl animate-pulse flex items-center justify-center">
                            <Loader2 className="h-8 w-8 text-gray-500 animate-spin" />
                          </div>
                        )}
                        <img
                          key={currentItem.imageUrl}
                          ref={handleImageRef}
                          src={currentItem.imageUrl}
                          alt={currentItem.title}
                          crossOrigin="anonymous"
                          className={`w-40 h-40 sm:w-48 sm:h-48 short:w-32 short:h-32 object-contain rounded-2xl shadow-xl border-4 transition-opacity duration-300 bg-gray-800 ${
                            scanSuccess
                              ? 'border-green-500'
                              : scanError
                              ? 'border-red-500'
                              : 'border-gray-600'
                          } ${imageState === 'loading' ? 'opacity-0' : 'opacity-100'}`}
                          onLoad={() => setImageState('loaded')}
                          onError={() => setImageState('error')}
                        />
                      </>
                    ) : (
                      <div className={`w-40 h-40 sm:w-48 sm:h-48 short:w-32 short:h-32 rounded-2xl flex flex-col items-center justify-center border-4 ${
                        scanSuccess
                          ? 'bg-green-500/20 border-green-500'
                          : scanError
                          ? 'bg-red-500/20 border-red-500'
                          : 'bg-gray-700 border-gray-600'
                      }`}>
                        <ImageOff className="h-12 w-12 sm:h-16 sm:w-16 text-gray-500 mb-2" />
                        <span className="text-2xl sm:text-3xl font-bold text-gray-400">
                          {getInitials(currentItem.title)}
                        </span>
                      </div>
                    )}

                    {scanSuccess && (
                      <div className="absolute inset-0 flex items-center justify-center bg-green-500/40 rounded-2xl">
                        <CheckCircle2 className="h-16 w-16 sm:h-20 sm:w-20 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 text-center sm:text-left short:text-left">
                    <h3 className="text-xl sm:text-2xl short:text-lg font-bold text-white mb-2 leading-tight">
                      {currentItem.title}
                    </h3>
                    {currentItem.sku && (
                      <p className="text-base sm:text-lg text-gray-400 mb-3">
                        SKU: <span className="font-mono font-bold text-cyan-400">{currentItem.sku}</span>
                      </p>
                    )}

                    {(currentItem.colorName || currentItem.sizeName) && (
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-4">
                        {currentItem.colorName && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/15 border border-purple-500/40 text-purple-200 text-sm font-medium">
                            <span className="text-[10px] uppercase tracking-wider text-purple-300/70 font-semibold">Color</span>
                            <span className="font-bold">{currentItem.colorName}</span>
                          </span>
                        )}
                        {currentItem.sizeName && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm font-medium">
                            <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-semibold">Size</span>
                            <span className="font-bold">{currentItem.sizeName}</span>
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                      <div className="flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-cyan-500/20 rounded-xl border border-cyan-500/50">
                        <span className="text-3xl sm:text-4xl font-black text-cyan-400">
                          {currentItem.quantity}
                        </span>
                        <span className="text-base sm:text-lg text-cyan-300 font-medium">units</span>
                      </div>

                      {!currentItem.binLocation && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-gray-700 rounded-xl">
                          <MapPin className="h-5 w-5 text-gray-400" />
                          <span className="text-gray-400 font-medium">No location</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              </div>

              <div className="shrink-0 short:w-[22rem] short:overflow-y-auto short:overscroll-contain border-t short:border-t-0 short:border-l border-gray-800 p-4 sm:p-6 short:p-4 pb-keyboard">
              {scanError && (
                <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-base text-red-300 font-medium">{scanError}</p>
                </div>
              )}

              {scanWarning && !scanError && (
                <div className="mb-4 p-4 bg-amber-500/20 border border-amber-500/50 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-base text-amber-200 font-medium">{scanWarning}</p>
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-400">
                    Scan barcode / UHF tag or enter SKU
                  </label>
                  {scannerActive && (
                    <span className="flex items-center gap-2 text-xs text-green-400">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      Scanner Active
                    </span>
                  )}
                </div>
                <div className="relative">
                  <ScanLine className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-500" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={scanInput}
                    onChange={(e) => {
                      setScanInput(e.target.value);
                      setScanError(null);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={isManualEntry ? 'Type SKU/barcode...' : 'Scan barcode or UHF tag...'}
                    inputMode={isManualEntry ? 'text' : 'none'}
                    enterKeyHint="done"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className="w-full pl-14 pr-4 py-4 sm:py-5 short:py-3 text-lg sm:text-xl short:text-base bg-gray-800 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleSkipItem}
                  className="flex-1 py-4 sm:py-5 short:py-3 text-base sm:text-lg short:text-sm bg-gray-800 hover:bg-gray-700 border-gray-700"
                >
                  Skip Item
                </Button>
                <Button
                  onClick={handleManualConfirm}
                  className="flex-1 py-4 sm:py-5 short:py-3 text-base sm:text-lg short:text-sm bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-bold"
                >
                  Confirm Pick
                  <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 ml-2" />
                </Button>
              </div>

              {items.length > 1 && (
                <div className="mt-4 pt-4 border-t border-gray-800">{itemJumpChips}</div>
              )}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto min-h-0 p-6 sm:p-8 text-center pb-keyboard">
              <div className="w-24 h-24 sm:w-28 sm:h-28 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-green-500">
                <CheckCircle2 className="h-12 w-12 sm:h-14 sm:w-14 text-green-400" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Picking Complete!
              </h3>
              <p className="text-gray-400 text-lg mb-6">
                {pickedCount} of {items.length} items picked
                {hasSkippedItems && ` (${items.filter((i) => i.status === 'skipped').length} skipped)`}
              </p>

              {hasSkippedItems && (
                <div className="mb-6 p-4 bg-amber-500/20 border border-amber-500/50 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-amber-400">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Some items were skipped</span>
                  </div>
                </div>
              )}

              {finishError && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-red-400">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">{finishError}</span>
                  </div>
                </div>
              )}

              {finishResult?.success && (
                <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-green-400">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Order finished! Label generated and printed.</span>
                  </div>
                  {finishResult.trackingCode && (
                    <p className="text-sm text-green-300 mt-2 text-center">
                      Tracking: {finishResult.trackingCode}
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleFinishOrder}
                  disabled={finishing || completing}
                  size="lg"
                  className="flex-1 py-5 text-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold"
                >
                  {finishing ? (
                    <>
                      <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                      Finishing...
                    </>
                  ) : (
                    <>
                      Finish & Print Label
                      <Printer className="h-6 w-6 ml-2" />
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleComplete}
                  disabled={completing || finishing}
                  size="lg"
                  variant="secondary"
                  className="flex-1 py-5 text-lg bg-gray-800 hover:bg-gray-700 text-white font-bold"
                >
                  {completing ? (
                    <>
                      <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    <>
                      Exit (Mark Ready)
                      <ArrowRight className="h-6 w-6 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PickModal;
